import type {
  MarkupImageSurface,
  MarkupPdfPageSurface,
} from 'features/documentMarkup/editor/types'
import type { RolodexDocumentEntry } from 'features/files/types'

type ReportMarkupIdentity = Pick<
  RolodexDocumentEntry,
  'entry_id' | 'pageRotations' | 'rolodex_file_ref'
>

interface LoadedReportSource {
  resetKey: string
}

interface LoadedReportPdfPage extends LoadedReportSource {
  file: File
  pageNumber: number
  /** PDF points from the unrotated page viewport at scale 1. */
  widthPoints: number
  /** PDF points from the unrotated page viewport at scale 1. */
  heightPoints: number
}

interface LoadedReportImage extends LoadedReportSource {
  src: string
  alt?: string
  naturalWidth: number
  naturalHeight: number
}

const assertPositive = (name: string, value: number) => {
  if (!Number.isFinite(value) || value <= 0)
    throw new RangeError(`${name} must be a finite positive number`)
}

const sourceId = (report: ReportMarkupIdentity) => {
  if (!report.entry_id.trim())
    throw new TypeError('Report entry ID is required')
  const fileRef = report.rolodex_file_ref
  return `${report.entry_id}:${fileRef ? `${fileRef.entry_id}/${fileRef.path}` : 'loaded-source'}`
}

const pageRotation = (report: ReportMarkupIdentity, pageNumber: number) => {
  if (!Number.isInteger(pageNumber) || pageNumber < 1)
    throw new RangeError('PDF page number must be a positive integer')
  const rotation = report.pageRotations?.[String(pageNumber)] ?? 0
  if (![0, 90, 180, 270].includes(rotation))
    throw new RangeError(
      'Report page rotation must be 0, 90, 180, or 270 degrees'
    )
  return rotation
}

/**
 * Builds an editor surface from an already loaded report PDF page. The caller
 * supplies scale-1, unrotated PDF dimensions; this bridge applies report page
 * rotation before the editor checks the rendered intrinsic page dimensions.
 */
export const reportPdfMarkupSurface = (
  report: ReportMarkupIdentity,
  loadedPage: LoadedReportPdfPage
): MarkupPdfPageSurface => {
  const source = sourceId(report)
  const rotationDegrees = pageRotation(report, loadedPage.pageNumber)
  assertPositive('PDF page width', loadedPage.widthPoints)
  assertPositive('PDF page height', loadedPage.heightPoints)
  const quarterTurn = rotationDegrees === 90 || rotationDegrees === 270
  return {
    kind: 'pdf-page',
    id: `report:${source}:page:${loadedPage.pageNumber}`,
    resetKey: `${source}:${loadedPage.resetKey}`,
    file: loadedPage.file,
    pageNumber: loadedPage.pageNumber,
    rotationDegrees,
    width: quarterTurn ? loadedPage.heightPoints : loadedPage.widthPoints,
    height: quarterTurn ? loadedPage.widthPoints : loadedPage.heightPoints,
  }
}

/** Builds an image surface from an already loaded report image and natural size. */
export const reportImageMarkupSurface = (
  report: ReportMarkupIdentity,
  loadedImage: LoadedReportImage
): MarkupImageSurface => {
  const source = sourceId(report)
  if (!loadedImage.src.trim())
    throw new TypeError('Loaded image source is required')
  assertPositive('Image natural width', loadedImage.naturalWidth)
  assertPositive('Image natural height', loadedImage.naturalHeight)
  return {
    kind: 'image',
    id: `report:${source}:image`,
    resetKey: `${source}:${loadedImage.resetKey}`,
    src: loadedImage.src,
    alt: loadedImage.alt,
    width: loadedImage.naturalWidth,
    height: loadedImage.naturalHeight,
  }
}
